import { BadgeCheck, ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";
import { useGetList, useNotify } from "ra-core";

import { Button } from "@/components/ui/button";
import { StatusBadge } from "../data-display/StatusBadge";
import { unwrapFunctionMessage } from "../lib/functionErrors";
import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { useEntitlements } from "./useEntitlements";

const FEATURE_LABELS: Record<string, string> = {
  crm: "Full CRM & contact management",
  pipelines: "Visual deal pipelines",
  invoicing: "Invoicing & payment collection",
  payments: "Payments",
  followup_sequences: "Automated follow-up sequences",
  sms_messages: "Two-way SMS (messages/mo)",
  twoway_email: "Two-way email",
  calling_minutes: "Two-way calling (minutes/mo)",
  email_marketing_contacts: "Email marketing (contacts)",
  email_marketing_sends: "Email marketing (sends/mo)",
  mobile_app: "Mobile app (iOS & Android)",
  notifications: "Email & SMS notifications",
  reporting_standard: "Standard reporting dashboard",
  estimates: "Quotes & estimates",
  jobs: "Jobs & field service management",
  pricebook: "Pricebook & product catalog",
  calendar_booking: "Calendar & online booking",
  forms_builder: "Forms builder & lead capture",
  fb_ads_capture: "Facebook Ads lead capture",
  power_dialer: "Power Dialer",
  ai_call_summaries: "AI call summaries & transcriptions",
  workflow_automation: "Advanced workflow automation",
  campaigns: "Campaigns & bulk messaging",
  campaign_analytics: "Campaign analytics & A/B testing",
  mailchimp_integration: "Mailchimp integration",
  lead_routing: "Advanced pipeline & lead routing",
  lead_scoring: "Lead scoring",
  esignatures: "E-signatures",
  booking_pages: "Public booking pages",
  leaderboard: "Agent performance leaderboard",
  commissions: "Commission tracking & team goals",
  custom_reporting: "Custom reporting & analytics",
  multi_location: "Multi-location management",
  api_webhooks: "API access & webhooks",
  priority_support: "Priority support",
  advanced_segmentation: "Advanced campaign segmentation",
  custom_workflows: "Advanced/custom workflows",
  white_label: "White-label branding",
  custom_objects: "Custom objects & data modeling",
  sso_advanced_rbac: "SSO & advanced role-based permissions",
  bi_export: "Custom reporting / BI export",
  invoicing_automation: "Invoicing automation",
  route_optimization: "GPS route optimization",
  manager_pricing_view: "Manager pricing view",
  dedicated_account_manager: "Dedicated account manager",
  sla_support: "SLA-backed support",
};

const STATUS_LABELS: Record<string, string> = {
  trialing: "Trial",
  active: "Active",
  past_due: "Past due",
  canceled: "Canceled",
  paused: "Paused",
};

/**
 * Read-only view of the workspace's current plan and entitlements, resolved
 * from the real subscription. Plan changes/billing arrive with the Stripe
 * adapter, nothing here is decorative data.
 */
interface SubscriptionRow {
  id: string;
  status: string;
  seats: number;
  trial_ends_at: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  payment_method_summary: string | null;
}

interface PlanRow {
  id: string;
  key: string;
  name: string;
  price_monthly_cents: number | null;
  sort_order: number;
}

export const PlanPanel = () => {
  const { planName, planKey, subscriptionStatus, entitlements, isPending } =
    useEntitlements();
  const notify = useNotify();
  const [busy, setBusy] = useState<string | null>(null);

  const { data: subscriptions } = useGetList<SubscriptionRow>("subscriptions", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "created_at", order: "DESC" },
  });
  const subscription = (subscriptions ?? [])[0];
  const { data: plans } = useGetList<PlanRow>("plans", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "sort_order", order: "ASC" },
    filter: { active: true },
  });

  const billing = async (action: string, planTarget?: string) => {
    setBusy(planTarget ?? action);
    try {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "stripe-billing",
        {
          body: {
            action,
            workspace_id: getActiveWorkspaceId(),
            plan_key: planTarget,
            return_url: window.location.href,
          },
        },
      );
      if (error) {
        const context = (error as { context?: Response }).context;
        let message = error.message;
        if (context && typeof context.json === "function") {
          try {
            message = unwrapFunctionMessage(await context.json()) ?? message;
          } catch {
            // keep generic
          }
        }
        throw new Error(message);
      }
      const url = (data as { url?: string })?.url;
      if (url) window.location.href = url;
    } catch (caught) {
      notify(
        caught instanceof Error ? caught.message : "Billing action failed",
        { type: "info" },
      );
    } finally {
      setBusy(null);
    }
  };

  if (isPending) return null;
  if (!planKey) {
    return (
      <p className="text-sm text-muted-foreground">
        No plan information available for this workspace.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-lg font-semibold">{planName}</span>
        {subscriptionStatus ? (
          <StatusBadge
            label={STATUS_LABELS[subscriptionStatus] ?? subscriptionStatus}
            tone={subscriptionStatus === "active" ? "success" : "info"}
          />
        ) : null}
      </div>
      <div className="grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-2">
        {subscription ? (
          <>
            <span className="text-muted-foreground">
              Billable seats: {subscription.seats}
            </span>
            {subscription.status === "trialing" &&
            subscription.trial_ends_at ? (
              <span className="text-muted-foreground">
                Trial ends{" "}
                {new Date(subscription.trial_ends_at).toLocaleDateString(
                  "en-US",
                )}
              </span>
            ) : null}
            {subscription.current_period_end ? (
              <span className="text-muted-foreground">
                {subscription.cancel_at_period_end
                  ? "Cancels on "
                  : "Next billing date "}
                {new Date(subscription.current_period_end).toLocaleDateString(
                  "en-US",
                )}
              </span>
            ) : null}
            {subscription.payment_method_summary ? (
              <span className="text-muted-foreground">
                Payment method: {subscription.payment_method_summary}
              </span>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(plans ?? [])
          .filter((plan) => plan.key !== planKey)
          .map((plan) => {
            const current = (plans ?? []).find((p) => p.key === planKey);
            const upgrade =
              (plan.price_monthly_cents ?? 0) >
              (current?.price_monthly_cents ?? 0);
            return (
              <Button
                key={plan.key}
                size="sm"
                variant={upgrade ? "default" : "outline"}
                disabled={busy !== null}
                onClick={() => void billing("checkout", plan.key)}
              >
                {busy === plan.key ? (
                  <Loader2 className="mr-1 size-3.5 animate-spin" />
                ) : null}
                {upgrade ? "Upgrade to" : "Switch to"} {plan.name}
              </Button>
            );
          })}
        <Button
          size="sm"
          variant="outline"
          disabled={busy !== null}
          onClick={() => void billing("portal")}
        >
          {busy === "portal" ? (
            <Loader2 className="mr-1 size-3.5 animate-spin" />
          ) : (
            <ExternalLink className="mr-1 size-3.5" />
          )}
          Manage subscription
        </Button>
      </div>
      <p className="text-[11.5px] text-muted-foreground">
        This is your subscription TO Gravun. Money you collect from your own
        customers lives under Connections, Payments. Plan changes go through
        Stripe and take effect when Stripe confirms them, not before.
      </p>
      <ul className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {entitlements
          .filter((e) => e.enabled)
          .map((e) => (
            <li
              key={e.feature_key}
              className="flex items-center gap-2 text-[13px] text-foreground"
            >
              <BadgeCheck className="size-3.5 shrink-0 text-success" />
              <span>
                {FEATURE_LABELS[e.feature_key] ?? e.feature_key}
                {e.limit_value != null ? (
                  <span className="text-muted-foreground">
                    {" "}
                    , up to {Number(e.limit_value).toLocaleString()}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
      </ul>
    </div>
  );
};
